import React from "react"
import classNames from "classnames"

import {axios} from "@lib/helpers"

import FormErrorMessage from "@components/molecules/FormErrorMessage"

type MasterOption = {
  id: number
  name: string
}

type PropTypes = {
  sales_agent_master_ids?: number[]
  selected_masters?: MasterOption[]
  checked?: boolean
  search_url: string
  t: {
    sales_agent?: string
    sales_agent_name?: string
    no_option?: string
    placeholder?: {sales_agent_select?: string}
  }
  required?: string
  error?: string
}

const SalesAgentMasterSelectField: React.FC<PropTypes> = ({
  sales_agent_master_ids = [],
  selected_masters = [],
  checked = false,
  search_url,
  t,
  required,
  error,
}) => {
  const [__isCheck, __isCheckSet] = React.useState(
    checked || sales_agent_master_ids.length > 0
  )
  const [__selections, __selectionsSet] = React.useState<MasterOption[]>(
    selected_masters.filter((m) => sales_agent_master_ids.includes(m.id))
  )
  const [__query, __querySet] = React.useState("")
  const [__results, __resultsSet] = React.useState<MasterOption[]>([])
  const [__focused, __focusedSet] = React.useState(false)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const placeholderText = t.placeholder?.sales_agent_select || "Please select"

  React.useEffect(() => {
    __isCheckSet(checked || sales_agent_master_ids.length > 0)
    __selectionsSet(
      selected_masters.filter((m) => sales_agent_master_ids.includes(m.id))
    )
  }, [checked, sales_agent_master_ids, selected_masters])

  React.useEffect(() => {
    if (!__focused) return

    const timer = setTimeout(
      () => {
        axios
          .get(search_url, {q: __query})
          .then((res: {data?: MasterOption[]}) => {
            const selectedIds = new Set(__selections.map((s) => s.id))
            __resultsSet(
              (res.data || []).filter((item) => !selectedIds.has(item.id))
            )
          })
          .catch(() => {
            __resultsSet([])
          })
      },
      __query.trim() ? 300 : 0
    )

    return () => clearTimeout(timer)
  }, [__query, __focused, __selections, search_url])

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        __focusedSet(false)
        __querySet("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked
    __isCheckSet(next)
    if (!next) {
      __selectionsSet([])
      __querySet("")
      __resultsSet([])
      __focusedSet(false)
    }
  }

  const handleSelect = (item: MasterOption) => {
    __selectionsSet([...__selections, item])
    __querySet("")
    __resultsSet([])
    inputRef.current?.focus()
  }

  const handleRemove = (id: number) => {
    __selectionsSet(__selections.filter((s) => s.id !== id))
    inputRef.current?.focus()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    __querySet(e.target.value)
  }

  const handleOpen = () => {
    __focusedSet(true)
    inputRef.current?.focus()
  }

  return (
    <React.Fragment>
      <div className="AdminForm__row AdminForm__row--no-bdb">
        <label className="CheckBox CheckBox--secondary">
          <input
            type="checkbox"
            name="agency[sales_agent]"
            onChange={handleToggle}
            checked={__isCheck}
          />
          <div className="CheckBox__span">{t.sales_agent}</div>
        </label>
      </div>

      {__isCheck ? (
        <div className="AdminForm__row AdminForm__row--no-bdb AdminForm__row--pl">
          <label className="AdminForm__label" htmlFor="sales_agent_master_ids">
            <div className="AdminForm__label__span">{t.sales_agent_name}</div>
            <div className="AdminForm__label__required">{required}</div>
          </label>
          <div className="AdminForm__value">
            {__selections.map((item) => (
              <input
                key={item.id}
                type="hidden"
                name="agency[sales_agent_master_ids][]"
                value={item.id}
              />
            ))}
            <div
              className={classNames("SearchableSelect", {
                "SearchableSelect__wrap--focus": __focused,
              })}
              ref={wrapperRef}
            >
              <div
                className={classNames("SearchableSelect__wrap", {
                  "SearchableSelect__wrap--focus": __focused,
                })}
                onClick={handleOpen}
              >
                <div className="SearchableSelect__body">
                  <div className="SearchableSelect__tags">
                    {__selections.map((item) => (
                      <span
                        key={item.id}
                        className="SearchableSelect__tag"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove(item.id)
                        }}
                      >
                        {item.name}
                        <svg
                          viewBox="0 0 24 24"
                          className="SearchableSelect__tag-icon"
                        >
                          <path d="M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7a1 1 0 0 0-1.4 1.4L10.6 12l-4.9 4.9a1 1 0 0 0 1.4 1.4L12 13.4l4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4z" />
                        </svg>
                      </span>
                    ))}
                    <input
                      ref={inputRef}
                      type="text"
                      className="SearchableSelect__input SearchableSelect__input--multi"
                      id="sales_agent_master_ids"
                      placeholder={
                        __selections.length === 0 ? placeholderText : ""
                      }
                      value={__query}
                      onChange={handleChange}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpen()
                      }}
                      autoComplete="off"
                    />
                  </div>
                </div>
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="SearchableSelect__icon"
                >
                  <path strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {__focused && (
                <ul className="SearchableSelect__options-list">
                  {__results.length === 0 ? (
                    <li className="SearchableSelect__option SearchableSelect__option--no-option">
                      {t.no_option || "No match"}
                    </li>
                  ) : (
                    __results.map((item) => (
                      <li
                        key={item.id}
                        className="SearchableSelect__option"
                        onClick={() => handleSelect(item)}
                      >
                        <span>{item.name}</span>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <FormErrorMessage message={error} />
          </div>
        </div>
      ) : (
        <input type="hidden" name="agency[sales_agent_master_ids][]" value="" />
      )}
    </React.Fragment>
  )
}

export default SalesAgentMasterSelectField
